import React, { useState } from "react";
import "./App.css";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  Trees, ArrowLeft, MapPin, Activity, TrendingUp, 
  Layers, Package, Share2, Download, Map as MapIcon,
  Droplets, ThermometerSun, Wind, CheckCircle
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from "recharts";

// Hooks & Components
import { useForestDetail } from "./hooks/useForestDetail";
import LoadingSpinner from "./components/LoadingSpinner";

/** ==== Components ==== */

const StatCard = ({ icon: Icon, title, value, subValue, color = "#10b981" }) => (
  <div className="card">
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <Icon size={18} color={color} />
      <div className="card-title" style={{ fontSize: ".9rem" }}>{title}</div>
    </div>
    <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--text-primary)" }}>
      {value}
    </div>
    {subValue && (
      <div style={{ fontSize: ".85rem", color: "var(--text-muted)", marginTop: 4 }}>
        {subValue}
      </div>
    )}
  </div>
);

const PlotCard = ({ plot, onClick }) => {
  const healthColor = plot.health?.score >= 80 ? "#10b981" : 
                      plot.health?.score >= 60 ? "#f59e0b" : "#ef4444";
  
  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: "0 12px 24px rgba(0,0,0,0.1)" }}
      className="card"
      style={{ cursor: "pointer", padding: "16px" }}
      onClick={onClick}
    >
      {/* Plot Image */}
      {plot.media?.latestDroneImageUrl ? (
        <img 
          src={plot.media.latestDroneImageUrl} 
          alt={plot.name}
          style={{ 
            width: '100%', 
            height: 140, 
            objectFit: 'cover', 
            borderRadius: 8, 
            marginBottom: 12 
          }}
        />
      ) : (
        <div style={{ 
          width: '100%', 
          height: 140, 
          background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
          borderRadius: 8,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Trees size={40} color="white" opacity={0.6} />
        </div>
      )}

      {/* Plot Info */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h3 style={{ fontSize: "1.1rem", fontWeight: 600, margin: 0, marginBottom: 6 }}>
            {plot.name}
          </h3>
          <div style={{ fontSize: ".85rem", color: "var(--text-muted)", marginBottom: 8 }}>
            {plot.species} • {plot.growthStage?.replace('_', ' ')}
          </div>
        </div>
        <div 
          className="pill" 
          style={{ 
            background: `${healthColor}15`, 
            color: healthColor,
            fontSize: ".8rem",
            padding: "4px 10px"
          }}
        >
          ❤️ {plot.health?.score || 'N/A'}
        </div>
      </div>

      {/* Capacity Bar */}
      <div style={{ marginTop: 12 }}>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          fontSize: ".8rem", 
          marginBottom: 4,
          color: "var(--text-muted)"
        }}>
          <span>Capacity</span>
          <span>{plot.treesPlanted} / {plot.capacity} trees</span>
        </div>
        <div style={{ 
          width: '100%', 
          height: 6, 
          background: '#e2e8f0', 
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: `${(plot.treesPlanted / plot.capacity) * 100}%`, 
            height: '100%', 
            background: healthColor,
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Status Badge */}
      <div style={{ marginTop: 12 }}>
        <span 
          className="badge" 
          style={{ 
            textTransform: 'capitalize',
            background: plot.status === 'growing' ? '#10b98115' : '#e2e8f0',
            color: plot.status === 'growing' ? '#047857' : '#64748b'
          }}
        >
          {plot.status?.replace('_', ' ')}
        </span>
      </div>
    </motion.div>
  );
};

const CarbonImpactChart = ({ totalCarbonSequestered }) => {
  // Estimate breakdown for visualization
  const data = [
    { name: 'Sequestered', value: totalCarbonSequestered, color: '#10b981' },
    { name: 'Potential', value: totalCarbonSequestered * 0.3, color: '#6ee7b7' }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Carbon Impact</div>
        <span className="badge">Lifetime</span>
      </div>
      <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#10b981" }}>
          {totalCarbonSequestered.toFixed(1)} tCO₂e
        </div>
        <div style={{ fontSize: ".85rem", color: "var(--text-muted)" }}>
          Total Carbon Sequestered
        </div>
      </div>
    </div>
  );
};

const CapacityChart = ({ plots }) => {
  const chartData = plots.map(plot => ({
    name: plot.name.length > 15 ? plot.name.substring(0, 12) + '...' : plot.name,
    planted: plot.treesPlanted,
    available: plot.capacity - plot.treesPlanted
  }));

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Plot Capacity Distribution</div>
      </div>
      <div className="chart-lg">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: "#475569", fontSize: 12 }} 
              axisLine={{ stroke: "#cbd5e1" }} 
              tickLine={false} 
            />
            <YAxis 
              tick={{ fill: "#475569" }} 
              axisLine={{ stroke: "#cbd5e1" }} 
              tickLine={false} 
            />
            <Tooltip 
              contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1e293b" }} 
            />
            <Bar dataKey="planted" stackId="a" fill="#10b981" name="Trees Planted" />
            <Bar dataKey="available" stackId="a" fill="#6ee7b7" name="Available Capacity" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default function ForestDetail() {
  const { id: forestId } = useParams();
  const navigate = useNavigate();
  
  const { forest, plots, loading, error } = useForestDetail(forestId);

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="container" style={{ padding: 40, textAlign: 'center' }}>
      <h2>Error loading forest details</h2>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>
        Back to Dashboard
      </button>
    </div>
  );
  if (!forest) return (
    <div className="container" style={{ padding: 40, textAlign: 'center' }}>
      <h2>Forest not found</h2>
      <button className="btn btn-primary" onClick={() => navigate('/')}>
        Back to Dashboard
      </button>
    </div>
  );

  return (
    <div className="app-container">
      {/* Navbar */}
      <div className="navbar">
        <div 
          className="logo" 
          style={{ height: '60px', overflow: 'hidden', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img 
            src="/Lateral Original.png" 
            alt="Sango" 
            style={{ 
              height: '110px', 
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'center'
            }}
          />
        </div>
        <div className="nav-right">
          <button className="btn btn-ghost"><Share2 size={16}/> Share</button>
          <button className="btn btn-outline"><Download size={16}/> Report</button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Back Button */}
        <motion.button 
          whileHover={{ x: -4 }}
          className="btn btn-ghost" 
          style={{ marginBottom: 20, padding: "8px 12px" }}
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </motion.button>

        {/* Header Section */}
        <div className="card" style={{ padding: "24px", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <Trees size={40} color="#10b981" />
                <div>
                  <h1 style={{ fontSize: "2.2rem", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
                    {forest.name}
                  </h1>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 6, fontSize: ".95rem", color: "var(--text-muted)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <MapPin size={14} /> {forest.location?.region}, {forest.location?.country}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <MapIcon size={14} /> 
                      Lat: {forest.location?.center?._lat?.toFixed(4)}, 
                      Long: {forest.location?.center?._long?.toFixed(4)}
                    </div>
                  </div>
                </div>
              </div>
              
              <p style={{ 
                fontSize: ".95rem", 
                color: "var(--text-muted)", 
                lineHeight: 1.6,
                marginTop: 16,
                maxWidth: '800px'
              }}>
                {forest.description}
              </p>
            </div>
            
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: ".9rem", color: "var(--text-muted)", marginBottom: 4 }}>Status</div>
              <div 
                className="pill"
                style={{ 
                  fontSize: "1rem", 
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  background: forest.status === 'active' ? '#10b98115' : '#e2e8f0',
                  color: forest.status === 'active' ? '#047857' : '#64748b',
                  padding: '8px 16px'
                }}
              >
                {forest.status === 'active' && <CheckCircle size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                {forest.status}
              </div>
            </div>
          </div>

          {/* Thumbnail Gallery */}
          {forest.media?.thumbnailUrl && (
            <div style={{ marginTop: 20 }}>
              <img 
                src={forest.media.thumbnailUrl} 
                alt={forest.name}
                style={{ 
                  width: '100%', 
                  maxHeight: 300,
                  objectFit: 'cover', 
                  borderRadius: 12 
                }}
              />
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid-4" style={{ marginBottom: 24 }}>
          <StatCard 
            icon={Layers} 
            title="Total Area" 
            value={forest.metrics?.totalAreaHectares?.toLocaleString() || 0} 
            subValue="hectares"
            color="#10b981"
          />
          <StatCard 
            icon={Trees} 
            title="Tree Capacity" 
            value={forest.metrics?.totalCapacityTrees?.toLocaleString() || 0} 
            subValue={`${forest.metrics?.occupiedCapacityTrees?.toLocaleString() || 0} planted`}
            color="#059669"
          />
          <StatCard 
            icon={Activity} 
            title="Carbon Sequestered" 
            value={forest.metrics?.totalCarbonSequestered?.toFixed(1) || 0} 
            subValue="tCO₂e"
            color="#047857"
          />
          <StatCard 
            icon={Package} 
            title="Active Plots" 
            value={plots.length} 
            subValue={`${plots.filter(p => p.status === 'growing').length} growing`}
            color="#065f46"
          />
        </div>

        {/* Charts Row */}
        <div className="grid" style={{ gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 24 }}>
          <CapacityChart plots={plots} />
          <CarbonImpactChart totalCarbonSequestered={forest.metrics?.totalCarbonSequestered || 0} />
        </div>

        {/* Plots Section */}
        <div className="card" style={{ padding: "24px", marginBottom: 24 }}>
          <div className="card-header" style={{ marginBottom: 20 }}>
            <div className="card-title">Forest Plots ({plots.length})</div>
            <span className="badge">Click to view details</span>
          </div>

          {plots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <Trees size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>No plots available in this forest yet.</p>
            </div>
          ) : (
            <div className="grid-3" style={{ gap: 20 }}>
              {plots.map((plot) => (
                <PlotCard
                  key={plot.id}
                  plot={plot}
                  onClick={() => navigate(`/plot/${plot.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="footer">© {new Date().getFullYear()} Sango • Invest in African Living Assets</div>
    </div>
  );
}
